/**
 * Analysis engine barrel export and pipeline.
 * Wires parseArchive → analyzeConversation → aggregate into ArchiveAnalysis.
 *
 * @module engine
 */

export { parseArchive } from './parser.js';
export { walkConversationTree, extractMessageText, filterMessages, calculateImageTokens, extractImageParts, scanConversationImages, detectContentTypes, countToolCalls, countSystemTokens } from './tree-walker.js';
export { estimateTokens, getContextWindow, analyzeConversation } from './token-estimator.js';
export { analyzeReinstruction, analyzeMessageGaps, detectTopicRestarts, detectOrphanedChats, analyzeCrossChat, analyzeContextWaste } from './cross-chat-analyzer.js';
export { estimateRenderDelay, estimateStreamDuration, estimatePerceivedWait, summarizePerceivedWait, TTFB_DEFAULT_S } from './perceived-wait-estimator.js';
export { calculateChatGPTTax, computePerChatTax } from './chatgpt-tax.js';
export { computeJeansScore } from './jeans-score.js';

import { parseArchive } from './parser.js';
import { analyzeConversation } from './token-estimator.js';
import { analyzeCrossChat } from './cross-chat-analyzer.js';
import { calculateChatGPTTax, computePerChatTax } from './chatgpt-tax.js';
import { computeJeansScore } from './jeans-score.js';

/** @typedef {import('./types.js').ArchiveAnalysis} ArchiveAnalysis */
/** @typedef {import('./types.js').ProgressEvent} ProgressEvent */

/**
 * Analyze a full ChatGPT export archive.
 *
 * 1. Unzip and parse conversations.json
 * 2. Analyze each conversation (yield to event loop every 50)
 * 3. Aggregate statistics
 *
 * @param {File|ArrayBuffer|Uint8Array} archiveFile
 * @param {Object} [options]
 * @param {(event: ProgressEvent) => void} [options.onProgress]
 * @returns {Promise<ArchiveAnalysis>}
 */
export async function analyzeArchive(archiveFile, options = {}) {
  const { onProgress } = options;

  // Step 1: Parse archive
  const { conversations: rawConversations, archiveSizeBytes } =
    await parseArchive(archiveFile, { onProgress });

  // Step 2: Analyze each conversation
  const conversations = [];
  const totalCount = rawConversations.length;

  for (let i = 0; i < totalCount; i++) {
    const analysis = analyzeConversation(rawConversations[i]);
    conversations.push(analysis);

    // Yield to event loop every 50 conversations for UI responsiveness
    if (i % 50 === 49) {
      if (onProgress) {
        onProgress({
          stage: 'analyze',
          pct: Math.round(((i + 1) / totalCount) * 100),
          detail: `${i + 1} / ${totalCount} chats`,
        });
      }
      await new Promise((r) => setTimeout(r, 0));
    }
  }

  if (onProgress) {
    onProgress({ stage: 'analyze', pct: 100, detail: `${totalCount} / ${totalCount} chats` });
  }

  // Step 3: Aggregate
  let totalMessages = 0;
  let totalTokens = 0;
  let ghostZoneConversations = 0;
  const modelDistribution = {};
  const gizmoInventory = {};

  for (const conv of conversations) {
    totalMessages += conv.messageCount;
    totalTokens += conv.totalTokens;

    if (conv.ghostZoneMessageCount > 0) {
      ghostZoneConversations++;
    }

    const slug = conv.modelSlug || 'unknown';
    modelDistribution[slug] = (modelDistribution[slug] || 0) + 1;

    if (conv.gizmoId && conv.gizmoType === 'custom') {
      gizmoInventory[conv.gizmoId] = (gizmoInventory[conv.gizmoId] || 0) + 1;
    }
  }

  // Top 10 most bloated
  const topBloated = [...conversations]
    .sort((a, b) => b.bloatScore - a.bloatScore)
    .slice(0, 10);

  // Cross-chat analysis (mutates each conversation to attach reinstruction + messageGaps)
  const crossChat = analyzeCrossChat(conversations);

  // Strip message text — no longer needed after cross-chat analysis.
  // Keeps role, timestamp, updateTime, tokens for downstream metrics.
  for (const conv of conversations) {
    for (const msg of conv.messages) {
      msg.text = '';
    }
  }

  // ChatGPT Tax rollup
  const chatgptTax = calculateChatGPTTax({ conversations, crossChat });

  // Per-chat tax (for Fattest Chats comparison)
  // Jeans score (forward-looking next-message experience)
  for (const conv of conversations) {
    conv.chatTax = computePerChatTax(conv, crossChat);
    conv.jeansScore = computeJeansScore(conv, crossChat);
  }

  return {
    conversations,
    totalConversations: totalCount,
    totalMessages,
    totalTokens,
    modelDistribution,
    gizmoInventory,
    topBloated,
    ghostZoneConversations,
    archiveSizeBytes,
    crossChat,
    chatgptTax,
  };
}
